import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
  type Variants,
} from 'motion/react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import type { ButtonProps } from '../../lib/ui-variants';

/**
 * A button that reports its own outcome: label → spinner → check, then back.
 *
 * After https://chanhdai.com/components/status-button, with three deliberate
 * departures. The spinner is the app's own `Loader2`, and a rejected `onClick`
 * only returns the button to idle instead of rethrowing — the caller has
 * already said what went wrong (a toast, usually) and a rethrow here would
 * surface as an unhandled rejection.
 *
 * The point of it is that the answer arrives on the control that was pressed,
 * so nothing has to pop up elsewhere to say "done".
 *
 * The third departure: a press is never swallowed because the previous one is
 * still animating. On a boulder scoresheet a judge taps "Attempt" three times
 * in a row before a top, faster than the ~1.8s idle→loading→success→idle
 * cycle — gating on that would drop attempts on the floor. Every physical
 * click invokes `onClick`; a caller for whom the order of overlapping calls
 * matters (it usually does — "does a result exist yet" is exactly the kind of
 * question two racing calls answer differently) should serialize its own work
 * with `createSerialQueue` rather than lean on this button to do it.
 */
export type ButtonStatus = 'idle' | 'loading' | 'success';

const DEFAULT_SUCCESS_DURATION = 1500;

const swapVariants: Variants = {
  initial: { opacity: 0, y: 8, filter: 'blur(4px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, y: -8, filter: 'blur(4px)' },
};

const reducedMotionVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const swapTransition: Transition = {
  type: 'spring',
  duration: 0.3,
  bounce: 0,
};

const successVariants: Variants = {
  ...swapVariants,
  animate: {
    ...(swapVariants.animate as object),
    transition: {
      type: 'spring',
      duration: 0.45,
      bounce: 0.35,
      opacity: swapTransition,
      filter: swapTransition,
    },
  },
};

export type StatusButtonProps = Omit<ButtonProps, 'onClick' | 'children'> & {
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  onStatusChange?: (status: ButtonStatus) => void;
  successLabel?: ReactNode;
  successDuration?: number;
};

export function StatusButton({
  children,
  className,
  onClick,
  onStatusChange,
  successLabel,
  successDuration = DEFAULT_SUCCESS_DURATION,
  ...props
}: StatusButtonProps) {
  const [status, setStatus] = useState<ButtonStatus>('idle');
  const shouldReduceMotion = useReducedMotion();

  const changeStatus = (next: ButtonStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  };

  useEffect(() => {
    if (status !== 'success') return;

    const timeoutId = window.setTimeout(() => {
      setStatus('idle');
      onStatusChange?.('idle');
    }, successDuration);
    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, successDuration]);

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;

    changeStatus('loading');

    try {
      await onClick(event);
      changeStatus('success');
    } catch {
      changeStatus('idle');
    }
  };

  const variants = shouldReduceMotion ? reducedMotionVariants : swapVariants;
  const transition = shouldReduceMotion ? { duration: 0 } : swapTransition;

  const successContent = (
    <>
      <Check className="h-4 w-4" />
      {successLabel ?? <span className="sr-only">Success</span>}
    </>
  );

  return (
    <Button
      data-status={status}
      aria-busy={status === 'loading'}
      onClick={handleClick}
      className={cn(
        'inline-grid justify-items-center *:col-start-1 *:row-start-1 *:flex *:items-center',
        className
      )}
      {...props}
    >
      {/* Invisible copies hold the width, so the button does not resize as its
          content is swapped. */}
      <span aria-hidden className="invisible">
        {children}
      </span>
      <span aria-hidden className="invisible">
        {successContent}
      </span>

      <AnimatePresence initial={false}>
        {status === 'idle' && (
          <motion.span
            key="idle"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={transition}
          >
            {children}
          </motion.span>
        )}

        {status === 'loading' && (
          <motion.span
            key="loading"
            role="status"
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={transition}
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="sr-only">Loading</span>
          </motion.span>
        )}

        {status === 'success' && (
          <motion.span
            key="success"
            role="status"
            variants={shouldReduceMotion ? variants : successVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={transition}
          >
            {successContent}
          </motion.span>
        )}
      </AnimatePresence>
    </Button>
  );
}
